import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Phone, Lock } from 'lucide-react'
import { useAuth, useClientConfig } from '@workspace/public-utils'
import { Seo } from '@workspace/public-ui/components/Seo'

const StarLoginPage = () => {
  const config = useClientConfig()
  const [formData, setFormData] = useState({ mobile_number: '', last4: '' })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const { login } = useAuth()
  const navigate = useNavigate()

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      await login(formData.mobile_number, formData.last4)
      navigate('/dashboard')
    } catch (err: any) {
      console.error('Login error:', err)
      let errorMessage = '로그인에 실패했습니다.'
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail
      } else if (err.response?.status === 401) {
        errorMessage = '휴대폰 번호 또는 비밀번호가 올바르지 않습니다.'
      }
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/')
    }
  }

  return (
    <div className="min-h-screen bg-star-cream dark:bg-gray-950 py-12 px-4 sm:px-6 lg:px-8 transition-colors">
      <Seo title="로그인" canonicalPath="/auth/login" noindex />
      <div className="max-w-md mx-auto">
        {/* Navigation */}
        <div className="mb-6 flex justify-between items-center">
          <button
            onClick={handleGoBack}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            뒤로가기
          </button>
          <Link
            to="/"
            className="star-btn-gold !py-2 !px-4 !text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            홈으로
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <Link to="/" className="inline-block">
            <div
              className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5 shadow-lg"
              style={{ backgroundColor: config.logoIconColor }}
            >
              <span className="text-2xl text-white font-black">{config.logoIconText}</span>
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight text-star-navy dark:text-white mb-1">
              {config.brandName}
            </h1>
          </Link>
          <p className="text-gray-500 dark:text-gray-400 text-sm">당신의 행운이 시작되는 곳</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 border border-gray-100 dark:border-gray-800"
        >
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">로그인</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">계정에 로그인하여 서비스를 이용하세요</p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6 text-sm"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="mobile_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                휴대폰 번호
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="tel"
                  id="mobile_number"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-star-gold/50 focus:border-star-gold transition-colors"
                  placeholder="010-1234-5678"
                />
              </div>
            </div>

            <div>
              <label htmlFor="last4" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                비밀번호
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="password"
                  id="last4"
                  name="last4"
                  value={formData.last4}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-star-gold/50 focus:border-star-gold transition-colors"
                  placeholder="비밀번호를 입력하세요"
                />
              </div>
            </div>

            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 dark:border-gray-600 text-star-gold focus:ring-star-gold/50"
                />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">로그인 상태 유지</span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full star-btn-primary !rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                  로그인 중...
                </div>
              ) : (
                '로그인'
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  )
}

export default StarLoginPage
